import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

export default class VariantPicker extends Component {

  #pendingRequestUrl;

  #abortController;

  #listenerController;

  #checkedIndices = [];

  #radios = [];

  #labels = [];

  #labelWidths = [];

  #hasPills = [];

  #keyboardFocus = false;

  #measureFrame = null;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();

    this.#refreshOptionState();

    this.#listenerController = new AbortController();
    this.addEventListener('change', this.variantChanged.bind(this), {
      signal: this.#listenerController.signal,
    });
    this.#trackKeyboardFocus(this.#listenerController.signal);
    this.#resizeObserver.observe(this);

    this.#measureFrame = requestAnimationFrame(() => {
      this.#measureFrame = null;
      if (!this.isConnected) return;
      this.updateVariantPickerCss();
    });
  }

  #markKeyboardFocus(target) {
    if (!this.#keyboardFocus || !this.contains(target)) return;
    if (target instanceof HTMLInputElement && target.type === 'radio') target.parentElement?.setAttribute('data-keyboard-focus', '');
  }

  #trackKeyboardFocus(signal) {
    this.addEventListener('keydown', () => { this.#keyboardFocus = true; }, { signal });
    this.addEventListener('pointerdown', () => { this.#keyboardFocus = false; }, { signal });
    this.addEventListener('focusin', ({ target }) => this.#markKeyboardFocus(target), { signal });
    this.addEventListener(
      'focusout',
      ({ target }) => {
        if (target instanceof HTMLInputElement) target.parentElement?.removeAttribute('data-keyboard-focus');
      },
      { signal },
    );
  }

  updatedCallback() {
    super.updatedCallback();
    if (!this.isConnected) return;

    this.#refreshOptionState();
    this.updateVariantPickerCss();

    this.#markKeyboardFocus(document.activeElement);
  }

  #refreshOptionState() {

    this.#radios = [];
    this.#labels = [];
    this.#checkedIndices = [];
    this.#labelWidths = [];
    this.#hasPills = [];

    const fieldsets =   (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset, fieldsetIndex) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      const labels = radios.map((radio) => radio.parentElement);
      this.#radios.push(radios);
      this.#labels.push(labels);
      this.#hasPills.push(labels.some((label) => label?.querySelector('.variant-option__button-label__pill')));

      let currentIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (currentIndex === -1) currentIndex = radios.findIndex((radio) => radio.checked);
      const previousIndex = radios.findIndex((radio) => radio.dataset.previousChecked === 'true');

      const history = [];
      if (currentIndex !== -1) history.push(currentIndex);
      if (previousIndex !== -1 && previousIndex !== currentIndex) history.push(previousIndex);
      this.#checkedIndices.push(history);

      this.#syncSelectionState(
        fieldsetIndex,
        currentIndex === -1 ? undefined : currentIndex,
        previousIndex === -1 ? undefined : previousIndex,
      );
    });
  }

  #syncSelectionState(fieldsetIndex, currentIndex, previousIndex) {
    const fieldset =   (this.refs.fieldsets || [])[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    const labels = this.#labels[fieldsetIndex];

    if (!fieldset || !radios || !labels) return;

    if (currentIndex === undefined || !radios[currentIndex]) {
      if (fieldset.hasAttribute('data-selection-model')) fieldset.removeAttribute('data-selection-model');
      if (fieldset.getAttribute('data-selection-motion') !== 'none') {
        fieldset.setAttribute('data-selection-motion', 'none');
      }
      return;
    }

    const lastIndex = labels.length - 1;
    let motion = 'none';
    if (previousIndex !== undefined && previousIndex !== currentIndex) {

      if (labels.length >= 3 && previousIndex === lastIndex && currentIndex === 0) {
        motion = 'wrap-forward';
      } else if (labels.length >= 3 && previousIndex === 0 && currentIndex === lastIndex) {
        motion = 'wrap-backward';
      } else {
        motion = currentIndex > previousIndex ? 'forward' : 'backward';
      }
    }

    labels.forEach((label, index) => {
      if (!label) return;

      const side = index < currentIndex ? 'before' : index === currentIndex ? 'current' : 'after';
      const adjacent = previousIndex !== undefined && Math.abs(currentIndex - previousIndex) === 1;

      let pillSide = 'current';
      if (index !== currentIndex) {
        if (motion === 'forward') {
          pillSide = adjacent
            ? index < currentIndex
              ? 'right'
              : 'left'
            : index >= previousIndex && index < currentIndex
              ? 'right'
              : 'left';
        } else if (motion === 'backward') {
          pillSide = adjacent
            ? index < currentIndex
              ? 'right'
              : 'left'
            : index > currentIndex && index <= previousIndex
              ? 'left'
              : 'right';
        } else if (motion === 'wrap-backward') {
          pillSide = index > previousIndex && index < currentIndex ? 'right' : 'left';
        } else if (motion === 'wrap-forward') {
          pillSide = index > currentIndex && index < previousIndex ? 'left' : 'right';
        } else {
          pillSide = index < currentIndex ? 'right' : 'left';
        }
      }

      const current = radios[index]?.dataset.currentChecked === 'true' ? 'true' : 'false';
      const previous = radios[index]?.dataset.previousChecked === 'true' ? 'true' : 'false';

      if (label.getAttribute('data-selection-side') !== side) label.setAttribute('data-selection-side', side);
      if (label.getAttribute('data-pill-side') !== pillSide) label.setAttribute('data-pill-side', pillSide);
      if (label.getAttribute('data-current-checked') !== current) {
        label.setAttribute('data-current-checked', current);
      }
      if (label.getAttribute('data-previous-checked') !== previous) {
        label.setAttribute('data-previous-checked', previous);
      }
    });

    if (fieldset.getAttribute('data-selection-motion') !== motion) {
      fieldset.setAttribute('data-selection-motion', motion);
    }
    if (fieldset.getAttribute('data-selection-model') !== 'direct') {
      fieldset.setAttribute('data-selection-model', 'direct');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#listenerController?.abort();
    if (this.#measureFrame !== null) {
      cancelAnimationFrame(this.#measureFrame);
      this.#measureFrame = null;
    }
    this.#labelWidths = [];

    this.#abortController?.abort();
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '' } = selectedOption.dataset;
    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {

      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets =   (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];
    const labelWidths = this.#labelWidths[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset || !labelWidths) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? labelWidths[currentIndex] : undefined,
      previousWidth: previousIndex !== undefined ? labelWidths[previousIndex] : undefined,
    };
  }

  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  updateFieldsetCss(fieldsetIndex) {

    if (Number.isNaN(fieldsetIndex) || !this.#hasPills[fieldsetIndex]) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets =   (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {

          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex === inputIndex) {

            target.checked = true;
            this.#syncSelectionState(fieldsetIndex, inputIndex, undefined);
            this.updateFieldsetCss(fieldsetIndex);
          } else {
            if (currentIndex !== undefined && radios[currentIndex]) {
              radios[currentIndex].dataset.previousChecked = 'false';
            }
            if (previousIndex !== undefined && radios[previousIndex]) {
              radios[previousIndex].dataset.previousChecked = 'false';
            }

            checkedIndices.unshift(inputIndex);
            checkedIndices.length = Math.min(checkedIndices.length, 2);

            const newCurrentIndex = checkedIndices[0];
            const newPreviousIndex = checkedIndices[1];

            if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
              radios[newCurrentIndex].dataset.currentChecked = 'true';
            }

            if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
              radios[newPreviousIndex].dataset.previousChecked = 'true';
              radios[newPreviousIndex].dataset.currentChecked = 'false';
            }

            target.checked = true;
            this.#syncSelectionState(fieldsetIndex, newCurrentIndex, newPreviousIndex);
            this.updateFieldsetCss(fieldsetIndex);
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {

    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId =   (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (!source && this.dataset.templateProductMatch === 'true' && !selectedOption.dataset.connectedProductUrl) {
      const mainSectionId = this.closest('.shopify-section')?.id?.replace(/^shopify-section-/, '');
      if (mainSectionId) {
        return `${productUrl}?section_id=${encodeURIComponent(mainSectionId)}&${params.join('&')}`;
      }
    }

    return `${productUrl}?${params.join('&')}`;
  }

  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '' } = detail;

    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const abortController = this.#abortController;

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
        },
        promise: deferredEventPromise.promise,
      })
    );

    fetch(requestUrl, { signal: abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (this.#abortController !== abortController || abortController.signal.aborted) {
          deferredEventPromise.reject(new DOMException('Variant request superseded', 'AbortError'));
          return;
        }

        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        if (!this.isConnected) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: null,
              resource: null,
            },
          });
          return;
        }

        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        deferredEventPromise.reject(error);
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        if (this.#abortController === abortController) {
          this.#abortController = undefined;
        }
      });
  }

  updateVariantPicker(newHtml) {

    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      onBeforeUpdate(oldNode, newNode) {
        MORPH_OPTIONS.onBeforeUpdate?.(oldNode, newNode);

        if (oldNode instanceof HTMLInputElement && newNode instanceof HTMLInputElement && oldNode.type === 'radio') {
          const shouldBeChecked = oldNode.checked;
          if (shouldBeChecked) {
            newNode.setAttribute('checked', '');
          } else {
            newNode.removeAttribute('checked');
          }
          newNode.checked = shouldBeChecked;
        }
      },
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    this.updatedCallback();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets =   (this.refs.fieldsets || []);

    this.#labelWidths = this.#radios.map((radios, index) =>
      this.#hasPills[index] ? radios.map((radio) => radio.parentElement?.offsetWidth || 0) : [],
    );

    const measurements = fieldsets
      .map((_, index) => (this.#hasPills[index] ? this.#getFieldsetMeasurements(index) : null))
      .filter((m) => m !== null);

    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement.cloneNode(true));
  }

  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  getAllSelectedOptions() {

    const options = [];

    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  get selectedOptionsValues() {

    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
